import React from "react";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";

export default function Privacy() {
    return (
        <>
            <Navbar isDownloadVisible={false} />
            <section className="policy-section">
                <h1 className="policy-title">Privacy Policy</h1>
                <ol>
                    <li>
                        <h2 className="policy-section-title">Last Updated</h2>
                        <p>
                            <strong>Last updated: July 20, 2025</strong>
                        </p>
                        <p>
                            Oakmate ("we", "us", or "our") respects your privacy
                            and is committed to protecting your personal
                            information. This Privacy Policy explains what
                            information Oakmate collects, how we use it, and
                            your rights regarding that information. It applies
                            to the Oakmate Chrome extension and our website at
                            oakmate.app. By using Oakmate, you agree to the
                            practices described here.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Information We Collect
                        </h2>
                        <p>
                            <strong>Email Address:</strong> When you purchase or
                            activate a license, we collect your email address
                            for the sole purpose of verifying your license and
                            sending you important license information. We will
                            never use your email for marketing unless you
                            explicitly opt in (which we don't do by default).
                        </p>
                        <p>
                            <strong>Usage and Analytics Data:</strong> We use
                            Google Analytics (a third-party service) to collect
                            anonymized usage statistics and performance data.
                            For example, we may track how often features are
                            used or how many games are analyzed. Google
                            Analytics collects information such as device type,
                            browser, pages visited, and general location, but
                            not your name or email. The data collected is
                            aggregate and anonymized, helping us improve
                            Oakmate. (Google Analytics 4 automatically
                            anonymizes IP addresses by default,{" "}
                            <a
                                href="https://cookie-script.com"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                cookie-script.com
                            </a>
                            .)
                        </p>
                        <p>
                            <strong>Cookies and Similar Technologies:</strong>{" "}
                            Our website and extension may use cookies or similar
                            technologies to operate and to support Google
                            Analytics. For example, a cookie may remember your
                            preferences or session details. You can usually
                            disable cookies in your browser, but this may
                            prevent the service from working properly.
                        </p>
                        <p>
                            <strong>Public Chess Data:</strong> Oakmate
                            retrieves chess games via public APIs from Chess.com
                            and Lichess as needed to analyze your games. These
                            games are publicly available (e.g. games on public
                            profiles or archives), and Oakmate does not store or
                            share any of this game data. We fetch it only to
                            perform the analysis in your browser and then
                            discard it. Because these games are public
                            information, they are not treated as private
                            personal data.
                        </p>
                        <p>
                            We do not collect any other personal information
                            (such as your name, address, phone number, or
                            financial details) unless you provide it
                            voluntarily.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            How We Use Your Information
                        </h2>
                        <p>
                            <strong>License Verification:</strong> We use your
                            email address only to confirm that you have a valid
                            license to use Oakmate. This may include sending you
                            account or license confirmation emails.
                        </p>
                        <p>
                            <strong>Service Improvement:</strong> We use
                            analytics data (via Google Analytics) to understand
                            how our extension and website are used. This helps
                            us fix bugs, improve features, and measure
                            performance (
                            <a
                                href="https://secureprivacy.ai"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                secureprivacy.ai
                            </a>
                            ). For example, we might learn which analysis
                            features are most popular so we can optimize them.
                            All analytics data is used in aggregate; we do not
                            track individual users or identify you personally.
                        </p>
                        <p>
                            <strong>No Marketing Emails:</strong> We will never
                            send you marketing or promotional emails unless you
                            explicitly ask for them. We do not share your email
                            with marketing partners, and we do not sell your
                            personal information to anyone.
                        </p>
                        <p>
                            We will never use your personal data for purposes
                            other than those described here. In particular, we
                            do not sell or rent your data to advertisers or
                            other third parties (
                            <a
                                href="https://cppa.ca.gov"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                cppa.ca.gov
                            </a>
                            ).
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Google Analytics (Tracking)
                        </h2>
                        <p>
                            Oakmate uses Google Analytics 4 to collect anonymous
                            usage data. Google Analytics is a free web analytics
                            service offered by Google that helps us understand
                            how visitors interact with our website and extension
                            (
                            <a
                                href="https://secureprivacy.ai"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                secureprivacy.ai
                            </a>
                            ). When you use Oakmate, Google Analytics may
                            collect information like which pages or features you
                            use and how long you use them. Importantly, GA4 by
                            default anonymizes your IP address (it does not
                            store full IPs) (
                            <a
                                href="https://cookie-script.com"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                cookie-script.com
                            </a>
                            ). We do not receive your name, email, or other
                            personal identifiers from Google Analytics. The only
                            purpose is to analyze trends and improve the app.
                            You can opt out of Google Analytics by using tools
                            like the Google Analytics Opt-out Browser Add-on.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Sharing and Disclosure
                        </h2>
                        <p>
                            We will never sell or share your personal
                            information with third parties for marketing or
                            commercial purposes. The only third-party service we
                            use that processes your data is Google Analytics (as
                            described above) (
                            <a
                                href="https://secureprivacy.ai"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                secureprivacy.ai
                            </a>
                            ). Google may process analytics data on our behalf.
                            Google complies with privacy rules and participates
                            in the EU-US Data Privacy Framework (see Data
                            Transfers below). We may disclose information if
                            required by law (for example, to comply with a
                            subpoena or investigation) or to protect our legal
                            rights. In such cases, we will disclose only the
                            minimum information required by law. We do not share
                            data with any advertisers or data brokers.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">Data Security</h2>
                        <p>
                            We take reasonable measures to protect your data
                            from unauthorized access, loss, or alteration. We
                            use industry-standard security practices such as
                            encryption (e.g. HTTPS) and secure data storage.
                            However, no internet service can be 100% secure. We
                            encourage you to take precautions such as using
                            strong, unique passwords. If you believe your data
                            has been compromised, please contact us immediately.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            International Data Transfers
                        </h2>
                        <p>
                            Oakmate is a global service, and data may be
                            transferred to and processed in countries other than
                            your own. For example, Google Analytics data is
                            processed by Google servers (often in the United
                            States). In cases of international transfers (such
                            as EU user data to the U.S.), we rely on legally
                            approved safeguards. Google is certified under the
                            EU-U.S. Data Privacy Framework and uses Standard
                            Contractual Clauses to protect any transferred data
                            (
                            <a
                                href="https://policies.google.com"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                policies.google.com
                            </a>
                            ). Similarly, if Oakmate's servers or service
                            providers are located abroad, we ensure they comply
                            with applicable laws. By using Oakmate, you consent
                            to any such transfers.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Your Privacy Rights
                        </h2>
                        <p>
                            <strong>European (GDPR) Rights:</strong> If you are
                            in the European Economic Area (EEA) or the UK, you
                            have rights under the GDPR. You can request access
                            to the personal data we hold about you, and receive
                            a copy (
                            <a
                                href="https://ico.org.uk"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                ico.org.uk
                            </a>
                            ). You also have the right to correct or update any
                            inaccurate data we hold, and to request the deletion
                            of your data when it is no longer needed (
                            <a
                                href="https://ico.org.uk"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                ico.org.uk
                            </a>
                            ). You may ask us to restrict or stop processing
                            your data or to object to certain processing. If we
                            rely on your consent for any processing (e.g.
                            analytics), you can withdraw that consent at any
                            time. You also have the right to data portability —
                            to receive your data in a common format (
                            <a
                                href="https://ico.org.uk"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                ico.org.uk
                            </a>
                            ). Finally, you can lodge a complaint with a data
                            protection authority if you believe we have violated
                            your rights.
                        </p>
                        <p>
                            <strong>California (CCPA/CPRA) Rights:</strong> If
                            you are a California resident, you have specific
                            rights under California law. These include the right
                            to know what personal information we have collected
                            about you and how we use it, the right to correct
                            inaccurate personal data, and the right to delete
                            your personal information (
                            <a
                                href="https://cppa.ca.gov"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                cppa.ca.gov
                            </a>
                            ). You also have the right to limit the use of your
                            sensitive personal information (though we do not
                            collect sensitive data beyond email) and to opt out
                            of the sale of your personal data (
                            <a
                                href="https://cppa.ca.gov"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                cppa.ca.gov
                            </a>
                            ). Oakmate does not sell or share your personal
                            information beyond what is described here.
                            California law also prohibits us from discriminating
                            against you for exercising these rights (
                            <a
                                href="https://cppa.ca.gov"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                cppa.ca.gov
                            </a>
                            ).
                        </p>
                        <p>
                            To exercise any of these rights (access, correction,
                            deletion, etc.), please contact us at
                            contact@oakmate.app. We may need to verify your
                            identity before fulfilling requests. We will respond
                            to your request within the timeframes required by
                            law (typically 30 days).
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">
                            Children's Privacy
                        </h2>
                        <p>
                            Oakmate is not intended for children under the age
                            of 16. We do not knowingly collect personal
                            information from children. If you are under 16,
                            please do not use our services or provide personal
                            data to us. If we learn that we have collected data
                            from a child without verification of parental
                            consent, we will delete that data.
                        </p>
                    </li>
                    <li>
                        <h2 className="policy-section-title">Contact Us</h2>
                        <p>
                            If you have questions about this Privacy Policy, or
                            wish to exercise your rights, please contact us:
                        </p>
                        <p>
                            <strong>Email:</strong> contact@oakmate.app
                        </p>
                        <p>
                            We will do our best to address your concerns
                            promptly. If you feel your issue is not resolved,
                            EU/UK users may contact their local data protection
                            authority, and California users may contact the
                            California Attorney General's office. We may update
                            this Privacy Policy from time to time (for example,
                            to comply with legal changes). The "Last updated"
                            date at the top reflects the latest revision. We
                            encourage you to review this page periodically for
                            any updates.
                        </p>
                    </li>
                </ol>
            </section>
            <footer className="FooterSection-Container">
                <Footer />
            </footer>
        </>
    );
}
